// Paso 1 (PE-A4): continuidad del negocio (PE-42, DE 394 Art. 5 núm. 3) y transferencia de riesgos (Art. 5 núm. 5).
// Va en el paso 1 porque son datos de la empresa y la sede, no salen de los cálculos (el paso 5 es el plan de acción
// que sale de ellos). Tiene su propio botón: el resto del paso 1 se guarda aunque la base no tenga M37.
// Lo que falte queda "por validar" y va al anexo de pendientes del PDF; no impide aprobar. La póliza es informativa.

import React, { useEffect, useState } from 'react';
import type { FilaPlan } from '../../lib/pe/pe-datos';
import {
  ART_CONTINUIDAD, ART_TRANSFERENCIA, Continuidad, continuidadDe, continuidadVacia, faltantesContinuidad, Transferencia, transferenciaDe,
} from '../../lib/pe/pe-continuidad';
import { leerDecimalEc } from '../../lib/pe/pe-unidades';
import { Campo } from './ComponentesPE';

export interface PropsContinuidad {
  plan: FilaPlan;
  ro: boolean;
  guardando: boolean;
  onGuardar: (c: Continuidad, t: Transferencia) => Promise<boolean>;
}

const aTexto = (n: number | null) => (n === null ? '' : String(n).replace('.', ','));

export default function Paso1Continuidad({ plan, ro, guardando, onGuardar }: PropsContinuidad) {
  const [c, setC] = useState<Continuidad>(() => (plan.business_continuity ? continuidadDe(plan.business_continuity) : continuidadVacia()));
  const [horas, setHoras] = useState<string[]>(() => c.procesos.map(p => aTexto(p.horas)));
  const [t, setT] = useState<Transferencia>(() => transferenciaDe(plan));
  const [errores, setErrores] = useState<string[]>([]);
  useEffect(() => {
    const n = plan.business_continuity ? continuidadDe(plan.business_continuity) : continuidadVacia();
    setC(n); setHoras(n.procesos.map(p => aTexto(p.horas))); setT(transferenciaDe(plan));
  }, [plan]);

  const conHoras = (): Continuidad | null => {
    const e: string[] = [];
    const procesos = c.procesos.map((p, i) => {
      const h = horas[i]?.trim() ? leerDecimalEc(horas[i]) : null;
      if (horas[i]?.trim() && (h === null || Number.isNaN(h))) e.push(`Proceso ${i + 1}: el tiempo máximo tolerable no es un número.`);
      return { ...p, horas: h === null || Number.isNaN(h) ? null : h };
    });
    setErrores(e);
    return e.length ? null : { ...c, procesos };
  };
  const guardar = async () => {
    const lista = conHoras();
    if (!lista) return;
    const ok = await onGuardar(lista, t);
    if (ok) setErrores([]);
  };
  const falta = faltantesContinuidad(c);
  const texto = (k: 'sitio_alterno' | 'proveedores' | 'equipos' | 'respaldo', etiqueta: string, nota?: string) => (
    <Campo etiqueta={etiqueta} nota={nota}>
      <textarea value={c[k]} disabled={ro} onChange={e => setC({ ...c, [k]: e.target.value })} />
    </Campo>
  );

  return (
    <section className="pe2-card" aria-label="Continuidad del negocio y transferencia de riesgos">
      <h3>Continuidad del negocio</h3>
      <small className="pe2-fuente-doc">DE 394, {ART_CONTINUIDAD}. Lo que falte queda por validar en el anexo de pendientes; no impide aprobar.</small>
      {falta.length > 0 && <div className="pe2-aviso">Por validar: {falta.join('; ')}.</div>}

      <b>Procesos o servicios críticos</b>
      <div className="pe2-lista-editable">
        {c.procesos.map((p, i) => (
          <div key={i} className="pe2-fila-editable">
            <Campo etiqueta={`Proceso ${i + 1}`}>
              <input type="text" value={p.nombre} disabled={ro}
                onChange={e => setC({ ...c, procesos: c.procesos.map((x, j) => (j === i ? { ...x, nombre: e.target.value } : x)) })} />
            </Campo>
            <Campo etiqueta="Tiempo máximo tolerable (h)">
              <input type="text" inputMode="decimal" value={horas[i] ?? ''} disabled={ro} aria-label={`Horas del proceso ${i + 1}`}
                onChange={e => setHoras(horas.map((h, j) => (j === i ? e.target.value : h)))} />
            </Campo>
            {!ro && (
              <button type="button" className="pe2-btn" aria-label={`Quitar el proceso ${i + 1}`}
                onClick={() => { setC({ ...c, procesos: c.procesos.filter((_, j) => j !== i) }); setHoras(horas.filter((_, j) => j !== i)); }}>Quitar</button>
            )}
          </div>
        ))}
        {!ro && (
          <div className="pe2-acciones">
            <button type="button" className="pe2-btn" onClick={() => { setC({ ...c, procesos: [...c.procesos, { nombre: '', horas: null }] }); setHoras([...horas, '']); }}>
              Agregar proceso
            </button>
          </div>
        )}
      </div>

      <div className="pe2-grid">
        {texto('sitio_alterno', 'Sitio alterno', 'Dónde se sigue operando si la sede no se puede usar.')}
        {texto('proveedores', 'Proveedores alternos')}
        {texto('equipos', 'Equipos de respaldo')}
        {texto('respaldo', 'Respaldo de información', 'Qué se respalda, cada cuánto y dónde.')}
      </div>

      <b>Responsables y contactos clave</b>
      <div className="pe2-lista-editable">
        {c.contactos.map((x, i) => (
          <div key={i} className="pe2-grid">
            <Campo etiqueta={`Contacto ${i + 1}: nombre`}>
              <input type="text" value={x.nombre} disabled={ro} onChange={e => setC({ ...c, contactos: c.contactos.map((y, j) => (j === i ? { ...y, nombre: e.target.value } : y)) })} />
            </Campo>
            <Campo etiqueta="Cargo o función">
              <input type="text" value={x.cargo} disabled={ro} onChange={e => setC({ ...c, contactos: c.contactos.map((y, j) => (j === i ? { ...y, cargo: e.target.value } : y)) })} />
            </Campo>
            <Campo etiqueta="Teléfono">
              <input type="tel" value={x.telefono} disabled={ro} onChange={e => setC({ ...c, contactos: c.contactos.map((y, j) => (j === i ? { ...y, telefono: e.target.value } : y)) })} />
            </Campo>
            {!ro && (
              <div className="pe2-acciones">
                <button type="button" className="pe2-btn" onClick={() => setC({ ...c, contactos: c.contactos.filter((_, j) => j !== i) })}>Quitar contacto</button>
              </div>
            )}
          </div>
        ))}
        {!ro && (
          <div className="pe2-acciones">
            <button type="button" className="pe2-btn" onClick={() => setC({ ...c, contactos: [...c.contactos, { nombre: '', cargo: '', telefono: '' }] })}>Agregar contacto</button>
          </div>
        )}
      </div>

      <Campo etiqueta="Pasos para reanudar operaciones" nota="Un paso por línea, en orden.">
        <textarea value={c.pasos.join('\n')} disabled={ro} onChange={e => setC({ ...c, pasos: e.target.value.split('\n') })} />
      </Campo>

      <h3>Transferencia de riesgos</h3>
      <small className="pe2-fuente-doc">DE 394, {ART_TRANSFERENCIA}. Informativo: no cambia ningún estado de cumplimiento.</small>
      <div className="pe2-grid">
        <Campo etiqueta="¿Tiene póliza u otro mecanismo de transferencia de riesgos?">
          <select value={t.tiene === null ? '' : t.tiene ? 'si' : 'no'} disabled={ro}
            onChange={e => setT({ ...t, tiene: e.target.value === '' ? null : e.target.value === 'si' })}>
            <option value="">Sin responder</option>
            <option value="si">Sí</option>
            <option value="no">No</option>
          </select>
        </Campo>
        {t.tiene && (
          <>
            <Campo etiqueta="Tipo" nota="Por ejemplo: póliza de incendio y líneas aliadas.">
              <input type="text" maxLength={120} value={t.tipo} disabled={ro} onChange={e => setT({ ...t, tipo: e.target.value })} />
            </Campo>
            <Campo etiqueta="Aseguradora">
              <input type="text" maxLength={120} value={t.aseguradora} disabled={ro} onChange={e => setT({ ...t, aseguradora: e.target.value })} />
            </Campo>
            <Campo etiqueta="Vigente hasta">
              <input type="date" value={t.vigenteHasta} disabled={ro} onChange={e => setT({ ...t, vigenteHasta: e.target.value })} />
            </Campo>
          </>
        )}
      </div>

      {errores.length > 0 && <div className="pe2-error" role="alert">{errores.join(' ')}</div>}
      {!ro && (
        <div className="pe2-acciones">
          <button type="button" className="pe2-btn primario" disabled={guardando} onClick={guardar}>Guardar continuidad y póliza</button>
        </div>
      )}
    </section>
  );
}
